import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack,
  Typography, FormControlLabel, Checkbox, Divider, Box, Alert,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { rolesApi, type Role } from '../../api/roles.api';

interface Props {
  open: boolean;
  initial?: Role | null;
  onClose: () => void;
  onSubmit: (input: { code: string; name: string; description?: string; permissionIds: number[] }) => Promise<void>;
  error?: string | null;
}

export function RoleFormDialog({ open, initial, onClose, onSubmit, error }: Props) {
  const isEdit = Boolean(initial);
  const { data: permissions = [] } = useQuery({ queryKey: ['permissions'], queryFn: rolesApi.listPermissions });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  useEffect(() => {
    setCode(initial?.code ?? '');
    setName(initial?.name ?? '');
    setDescription(initial?.description ?? '');
    setSelected(new Set(initial?.permissionIds ?? []));
  }, [initial, open]);

  const grouped = useMemo(() => {
    const map: Record<string, typeof permissions> = {};
    for (const p of permissions) (map[p.module] ??= []).push(p);
    return map;
  }, [permissions]);

  const toggle = (id: number) =>
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? `Edit Role: ${initial?.name}` : 'Create Role'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={2}>
            <TextField label="Code" value={code} onChange={(e) => setCode(e.target.value)} disabled={isEdit} helperText="lowercase_with_underscores" />
            <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
          </Stack>
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth />
          <Divider />
          <Typography variant="subtitle2">Permissions</Typography>
          {Object.entries(grouped).map(([module, perms]) => (
            <Box key={module}>
              <Typography variant="caption" color="text.secondary" textTransform="uppercase">{module}</Typography>
              <Box display="flex" flexWrap="wrap">
                {perms.map((p) => (
                  <FormControlLabel
                    key={p.id}
                    control={<Checkbox size="small" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />}
                    label={p.action}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit({ code, name, description: description || undefined, permissionIds: [...selected] })}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
